import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Loader2, Copy, Check, Sparkles, AlignRight, Camera, Mic, MicOff, Languages, X, Upload, Volume2, VolumeX, History, Trash2 } from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import api from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { cn } from "../lib/utils";

export default function SummarizePage() {
  const { t, isRTL } = useLanguage();
  const [text, setText] = useState("");
  const [summary, setSummary] = useState("");
  const [translation, setTranslation] = useState("");
  const [loading, setLoading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [extractingText, setExtractingText] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const audioRef = useRef(null);

  // ========== جلب السجل ==========
  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await api.get(`/summarize/history`);
      setHistory(response.data);
    } catch (error) {
      console.error("Failed to fetch history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const deleteHistoryItem = async (id) => {
    try {
      await api.delete(`/summarize/history/${id}`);
      setHistory(prev => prev.filter(item => item.id !== id));
      toast.success("تم الحذف");
    } catch (error) {
      toast.error("خطأ في الحذف");
    }
  };

  const loadFromHistory = (item) => {
    setText(item.original_text);
    setSummary(item.summary);
    setTranslation("");
    setShowHistory(false);
    toast.success("تم تحميل الملخص");
  };

  // ========== تحويل النص لصوت ==========
  const speakText = async (textToSpeak) => {
    if (isSpeaking) {
      // Stop current audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsSpeaking(false);
      return;
    }

    setIsSpeaking(true);
    try {
      const response = await api.post(`/tts/speak`, {
        text: textToSpeak,
        voice: "alloy",
        speed: 1.0
      });

      const audio = new Audio(`data:audio/mp3;base64,${response.data.audio_base64}`);
      audioRef.current = audio;
      
      audio.onended = () => {
        setIsSpeaking(false);
        audioRef.current = null;
      };
      
      audio.onerror = () => {
        setIsSpeaking(false);
        audioRef.current = null;
        toast.error("خطأ في تشغيل الصوت");
      };
      
      await audio.play();
    } catch (error) {
      console.error("TTS error:", error);
      toast.error("خطأ في تحويل النص لصوت");
      setIsSpeaking(false);
    }
  };

  useEffect(() => {
    // Cleanup audio on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  // ========== الكاميرا ==========
  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment', width: 1280, height: 720 } 
      });
      streamRef.current = stream;
      setShowCamera(true);
      // Wait for modal to render then attach stream
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 100);
    } catch (error) {
      console.error("Camera error:", error);
      toast.error("خطأ في فتح الكاميرا - تأكد من إعطاء الصلاحية");
    }
  };

  const closeCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
    setCapturedImage(null);
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth || 1280;
    canvas.height = videoRef.current.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);
    
    const imageData = canvas.toDataURL('image/jpeg', 0.9);
    setCapturedImage(imageData);
    
    // Stop video
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
  };

  const extractFromCaptured = async () => {
    if (!capturedImage) return;
    
    setExtractingText(true);
    try {
      const base64 = capturedImage.split(',')[1];
      const response = await api.post(`/image/extract-text`, {
        image_base64: base64
      });
      
      if (response.data.text) {
        setText(response.data.text);
        toast.success("تم استخراج النص بنجاح");
        closeCamera();
      }
    } catch (error) {
      console.error("Text extraction error:", error);
      toast.error("خطأ في استخراج النص");
    } finally {
      setExtractingText(false);
    }
  };

  // ========== رفع صورة ==========
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("يرجى اختيار صورة");
      return;
    }

    setExtractingText(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const base64 = e.target.result.split(',')[1];
        const response = await api.post(`/image/extract-text`, {
          image_base64: base64
        });
        
        if (response.data.text) {
          setText(response.data.text);
          toast.success("تم استخراج النص بنجاح");
        }
      } catch (error) {
        console.error("Text extraction error:", error);
        toast.error("خطأ في استخراج النص");
      } finally {
        setExtractingText(false);
      }
    };
    reader.readAsDataURL(file);
    
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ========== الصوت ==========
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        
        // تحويل الصوت لنص
        setLoading(true);
        try {
          const formData = new FormData();
          formData.append('file', audioBlob, 'recording.webm');
          const response = await api.post(`/speech/transcribe`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            withCredentials: true
          });
          if (response.data.text) {
            setText(response.data.text);
            toast.success("تم تحويل الصوت بنجاح");
          }
        } catch (error) {
          toast.error("خطأ في تحويل الصوت");
        } finally {
          setLoading(false);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.info("جاري التسجيل...");
    } catch (error) {
      toast.error("خطأ في الميكروفون");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // ========== التلخيص ==========
  const handleSummarize = async (e) => {
    e.preventDefault();
    if (!text.trim() || loading) return;

    setLoading(true);
    setSummary("");
    setTranslation("");

    try {
      const response = await api.post(`/summarize`, { text: text.trim() });
      setSummary(response.data.summary);
      toast.success("تم التلخيص بنجاح");
    } catch (error) {
      toast.error("خطأ في التلخيص");
    } finally {
      setLoading(false);
    }
  };

  // ========== الترجمة ==========
  const handleTranslate = async () => {
    if (!summary || translating) return;
    
    setTranslating(true);
    try {
      const response = await api.post(`/chat`, {
        session_id: `translate-${Date.now()}`,
        message: `ترجم هذا النص. إذا كان بالعربية ترجمه للإنجليزية، وإذا كان بالإنجليزية ترجمه للعربية. أعطني الترجمة فقط:\n\n${summary}`
      });
      
      setTranslation(response.data.response);
      toast.success("تمت الترجمة بنجاح");
    } catch (error) {
      toast.error("خطأ في الترجمة");
    } finally {
      setTranslating(false);
    }
  };

  const handleCopy = (textToCopy) => {
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    toast.success("تم النسخ");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen p-4 pt-20 md:pt-4 pb-24 md:pb-6 lg:p-6 lg:pt-6 bg-background" data-testid="summarize-page">
      <div className="relative z-10 max-w-5xl mx-auto">
        
        {/* Header */}
        <motion.header initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-5">
          <div className="flex items-center gap-2.5">
            <div className="p-2.5 rounded-xl bg-foreground text-background">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-foreground">تلخيص النصوص</h1>
              <p className="text-[10px] text-muted-foreground">HELCOO AI</p>
            </div>
          </div>
        </motion.header>

        {/* أزرار الإدخال */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={openCamera}
            className="rounded-xl px-3 py-2 flex items-center gap-1.5 bg-muted text-foreground text-sm"
            data-testid="camera-btn"
          >
            <Camera className="w-4 h-4" />
            الكاميرا
          </button>

          <button
            onClick={() => fileInputRef.current?.click()}
            className="rounded-xl px-3 py-2 flex items-center gap-1.5 bg-muted text-foreground text-sm"
            data-testid="upload-btn"
          >
            <Upload className="w-4 h-4" />
            رفع صورة
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />

          <button
            onClick={isRecording ? stopRecording : startRecording}
            className={cn(
              "rounded-xl px-3 py-2 flex items-center gap-1.5 text-sm",
              isRecording ? "bg-red-500/15 text-red-500" : "bg-muted text-foreground"
            )}
            data-testid="audio-btn"
          >
            {isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            {isRecording ? "إيقاف" : "الصوت"}
          </button>

          <button
            onClick={handleTranslate}
            disabled={!summary || translating}
            className={cn(
              "rounded-xl px-3 py-2 flex items-center gap-1.5 text-sm transition-all",
              summary ? "bg-foreground text-background" : "bg-muted text-muted-foreground"
            )}
            data-testid="translate-btn"
          >
            {translating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" />}
            ترجمة
          </button>

          <button
            onClick={() => speakText(translation || summary)}
            disabled={!summary}
            className={cn(
              "rounded-xl px-3 py-2 flex items-center gap-1.5 text-sm",
              isSpeaking ? "bg-green-500/15 text-green-500" : "bg-muted text-foreground",
              !summary && "opacity-50"
            )}
            data-testid="speak-btn"
          >
            {isSpeaking ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            {isSpeaking ? "إيقاف" : "استماع"}
          </button>

          <button
            onClick={() => { setShowHistory(true); fetchHistory(); }}
            className="rounded-xl px-3 py-2 flex items-center gap-1.5 bg-muted text-foreground text-sm"
            data-testid="history-btn"
          >
            <History className="w-4 h-4" />
            السجل
          </button>
        </div>

        {/* Loading للاستخراج */}
        {extractingText && (
          <div className="mb-6 p-4 rounded-xl bg-muted border border-border flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-foreground" />
            <span className="text-foreground">جاري استخراج النص من الصورة...</span>
          </div>
        )}

        {/* Modal السجل */}
        <AnimatePresence>
          {showHistory && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowHistory(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-background rounded-3xl p-6 w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                    <History className="w-5 h-5" />
                    سجل الملخصات
                  </h2>
                  <button
                    onClick={() => setShowHistory(false)}
                    className="p-2 rounded-full hover:bg-muted"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="flex-1 overflow-y-auto space-y-3">
                  {loadingHistory ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : history.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      لا يوجد سجل بعد
                    </div>
                  ) : (
                    history.map((item) => (
                      <div
                        key={item.id}
                        className="p-4 rounded-xl bg-muted border border-border hover:border-foreground/20 transition-all cursor-pointer group"
                        onClick={() => loadFromHistory(item)}
                      >
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                          {item.original_text}
                        </p>
                        <p className="text-foreground line-clamp-2 mb-2">
                          {item.summary}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            {(() => {
                              const d = new Date(item.created_at);
                              return `${d.getDate()}/${d.getMonth()+1}/${d.getFullYear()}`;
                            })()}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteHistoryItem(item.id); }}
                            className="p-1.5 rounded-full hover:bg-red-500/20 text-muted-foreground hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Modal الكاميرا */}
        <AnimatePresence>
          {showCamera && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black flex items-center justify-center p-4"
            >
              <div className="relative w-full max-w-2xl">
                {/* زر الإغلاق */}
                <button onClick={closeCamera} className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/20 text-white">
                  <X className="w-6 h-6" />
                </button>

                {!capturedImage ? (
                  <>
                    <video ref={videoRef} autoPlay playsInline muted className="w-full rounded-2xl bg-gray-900" />
                    <div className="flex justify-center mt-4">
                      <button onClick={capturePhoto} className="px-8 py-4 rounded-full bg-white text-black font-bold flex items-center gap-2">
                        <Camera className="w-5 h-5" />
                        التقاط
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <img src={capturedImage} alt="Captured" className="w-full rounded-2xl" />
                    <div className="flex justify-center gap-4 mt-4">
                      <button onClick={() => { setCapturedImage(null); openCamera(); }} className="px-6 py-3 rounded-full bg-white/20 text-white">
                        إعادة
                      </button>
                      <button onClick={extractFromCaptured} disabled={extractingText} className="px-6 py-3 rounded-full bg-white text-black font-bold flex items-center gap-2">
                        {extractingText ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
                        استخراج النص
                      </button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* قسم الإدخال */}
          <form onSubmit={handleSummarize}>
            <div className="rounded-3xl p-6 bg-card border border-border h-full flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <label className="font-semibold text-lg text-foreground">النص الأصلي</label>
                <span className="text-sm text-muted-foreground">{text.length} حرف</span>
              </div>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="الصق النص هنا أو استخدم الكاميرا/الصوت..."
                className="flex-1 min-h-[300px] resize-none text-base bg-transparent border-0 text-foreground placeholder:text-muted-foreground focus-visible:ring-0"
                disabled={loading}
              />
              <div className="flex gap-3 mt-4">
                <button
                  type="submit"
                  disabled={!text.trim() || loading}
                  className="bg-foreground text-background px-8 py-3 rounded-full flex items-center gap-2 flex-1 justify-center disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                  {loading ? "جاري التلخيص..." : "تلخيص"}
                </button>
                {(text || summary) && (
                  <button type="button" onClick={() => { setText(""); setSummary(""); setTranslation(""); }} className="rounded-full px-6 bg-muted border border-border text-muted-foreground hover:text-foreground">
                    مسح
                  </button>
                )}
              </div>
            </div>
          </form>

          {/* قسم النتيجة */}
          <div className="rounded-3xl p-6 bg-card border border-border h-full flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <label className="font-semibold text-lg flex items-center gap-2 text-foreground">
                <AlignRight className="w-5 h-5 text-muted-foreground" />
                الملخص
              </label>
              
              {/* زر النسخ */}
              {summary && (
                <button
                  onClick={() => handleCopy(translation || summary)}
                  className="rounded-full px-4 py-2 flex items-center gap-2 text-sm bg-muted border border-border text-muted-foreground hover:text-foreground"
                  data-testid="copy-btn"
                >
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  {copied ? "تم" : "نسخ"}
                </button>
              )}
            </div>
            
            <div className="flex-1">
              {loading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full border-4 border-border border-t-foreground animate-spin mx-auto" />
                    <p className="mt-4 text-muted-foreground">جاري التلخيص...</p>
                  </div>
                </div>
              ) : summary ? (
                <div className="space-y-4">
                  {/* الملخص */}
                  <div className="bg-muted rounded-2xl p-6">
                    <p className="whitespace-pre-wrap leading-relaxed text-foreground">{summary}</p>
                  </div>
                  
                  {/* الترجمة */}
                  {translation && (
                    <div className="bg-muted/50 rounded-2xl p-6 border border-border">
                      <div className="flex items-center gap-2 mb-3">
                        <Languages className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium text-muted-foreground">الترجمة</span>
                      </div>
                      <p className="whitespace-pre-wrap leading-relaxed text-foreground">{translation}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-center">
                  <div>
                    <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-muted border border-border flex items-center justify-center">
                      <FileText className="w-10 h-10 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground">سيظهر الملخص هنا</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
